import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Search, X, Edit2, Trash2, Package2, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TransactionForm from "@/components/TransactionForm";
import ExportBar from "@/components/ExportBar";
import { formatCurrency } from "@/lib/formatters";
import * as db from "@/lib/db";

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxTreatments, setTaxTreatments] = useState([]);
  const [events, setEvents] = useState([]);

  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [categoryId, setCategoryId] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [searchDeb, setSearchDeb] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDeb(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Locate the Product Purchase type id from taxonomy.
  const productPurchaseType = useMemo(
    () => types.find((t) => t.plTreatment === "product_purchase_cash_only" && !t.isArchived),
    [types]
  );

  const fetchStatic = useCallback(async () => {
    try {
      const [tt, cats, tax, ev] = await Promise.all([
        db.listTransactionTypes({ includeArchived: true }),
        db.listTransactionCategories({ includeArchived: true }),
        db.listTaxTreatmentStatuses({ includeArchived: true }),
        db.listEventsSummary({ sortBy: "latest" }),
      ]);
      setTypes(tt); setCategories(cats); setTaxTreatments(tax); setEvents(ev);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!productPurchaseType) return;
    try {
      setLoading(true);
      const rows = await db.listTransactions({
        from: from || null, to: to || null,
        transactionTypeId: productPurchaseType.id,
        categoryId,
        sortBy: "date_desc",
        search: searchDeb,
      });
      setPurchases(rows);
    } catch (e) { console.error(e); toast.error("Failed to load purchases"); }
    finally { setLoading(false); }
  }, [productPurchaseType, from, to, categoryId, searchDeb]);

  useEffect(() => { fetchStatic(); }, [fetchStatic]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const purchaseCategories = useMemo(
    () => productPurchaseType ? categories.filter((c) => c.transactionTypeId === productPurchaseType.id) : [],
    [categories, productPurchaseType]
  );

  const total = useMemo(() => purchases.reduce((s, p) => s + Number(p.amount || 0), 0), [purchases]);
  const totalQty = useMemo(() => purchases.reduce((s, p) => s + (p.quantity != null ? Number(p.quantity) : 0), 0), [purchases]);

  const catById = (id) => categories.find((c) => c.id === id);
  const filtersActive = from || to || categoryId !== "all" || search.trim();
  const clearAll = () => { setFrom(""); setTo(""); setCategoryId("all"); setSearch(""); };

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setShowForm(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await db.deleteTransaction(deleteTarget.id);
      toast.success("Purchase deleted");
      setDeleteTarget(null); fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const selectClass = "w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm text-slate-800 appearance-none cursor-pointer transition-colors";

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="purchases-title">
              Purchases Register
            </h1>
            <p className="text-sm text-slate-600 mt-1">Cash-tracking for ingredient, cup, lid &amp; other product supplies</p>
          </div>
          <Button onClick={openNew} disabled={!productPurchaseType} className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full font-semibold h-11 px-5" data-testid="add-purchase-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Purchase
          </Button>
        </div>

        <ExportBar
          testIdPrefix="purchases-export"
          buildReport={() => {
            const cols = [
              { key: "txDate", label: "Date" },
              { key: "category", label: "Category" },
              { key: "supplierCustomer", label: "Supplier" },
              { key: "description", label: "Description" },
              { key: "quantity", label: "Quantity" },
              { key: "unitCost", label: "Unit Cost (RM)", type: "currency" },
              { key: "amount", label: "Total (RM)", type: "currency" },
              { key: "receiptNumber", label: "Receipt" },
              { key: "receiptStatus", label: "Status" },
            ];
            const rowsExp = purchases.map((p) => ({ ...p, category: catById(p.categoryId)?.name || "" }));
            const total = { txDate: "TOTAL", amount: rowsExp.reduce((s, r) => s + Number(r.amount || 0), 0) };
            return {
              title: "Purchases Register",
              subtitle: `${from || "…"} → ${to || "…"}`,
              filename: `Gao-Chill-Purchases-${from || "all"}${to ? `_to_${to}` : ""}`,
              columns: cols,
              rows: rowsExp,
              totals: total,
              sections: [{ heading: "Product Purchases", columns: cols, rows: rowsExp, totals: total }],
              metrics: [
                { label: "Records", value: purchases.length },
                { label: "Total Spent", value: formatCurrency(total.amount) },
              ],
              footnote: "These purchases reduce Monthly P&L. Per-event Products Sold cost remains only for internal Event P&L analysis and is not deducted again in Monthly P&L.",
            };
          }}
        />

        <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-xs text-sky-800 flex items-start gap-2" data-testid="purchases-notice">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Purchases Register vs Product Cost:</strong> These are <strong>different records</strong>. Purchases logged here track cash spent on ingredients / cups / lids / straws etc. The Product Cost of items <em>sold</em> is captured per-event in each Per Event P&amp;L. Purchases here are <strong>not</strong> deducted again in Monthly P&amp;L to avoid double-counting.
          </div>
        </div>

        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4" data-testid="purchases-filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search supplier, description, receipt no…" className="w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 pl-9 pr-9 text-sm" data-testid="purchases-search" />
            {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1" data-testid="purchases-search-clear"><X className="w-4 h-4" /></button>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">From Date</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={selectClass} data-testid="purchases-from" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">To Date</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={selectClass} data-testid="purchases-to" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">Category</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={selectClass} data-testid="purchases-category">
                <option value="all">All Categories</option>
                {purchaseCategories.map((c) => (<option key={c.id} value={c.id}>{c.name}{c.isArchived ? " (archived)" : ""}</option>))}
              </select>
            </div>
          </div>
          {filtersActive && (
            <div className="flex justify-end">
              <button onClick={clearAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d]" data-testid="purchases-reset">
                <X className="w-3.5 h-3.5" /> Reset Filters
              </button>
            </div>
          )}
        </section>

        <div className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-4 sm:p-5 shadow-lg grid grid-cols-3 gap-4" data-testid="purchases-summary">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Records</p>
            <p className="text-xl font-mono font-bold mt-1" data-testid="purchases-count">{purchases.length}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Total qty</p>
            <p className="text-xl font-mono font-bold mt-1" data-testid="purchases-total-qty">{totalQty > 0 ? totalQty.toLocaleString() : "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Total spent</p>
            <p className="text-xl font-mono font-bold mt-1" data-testid="purchases-total">{formatCurrency(total)}</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading…</div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl" data-testid="purchases-empty">
            <Package2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">{filtersActive ? "No purchases match your filters." : "No purchases logged yet"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {purchases.map((p) => (
              <div key={p.id} className="bg-white border border-slate-200 hover:border-[#193f7d]/40 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid={`purchase-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-semibold text-slate-500">{p.txDate}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5">{catById(p.categoryId)?.name || "—"}</span>
                    </div>
                    <p className="text-base font-semibold text-slate-900 mt-1 truncate">{p.description || <span className="italic text-slate-400">No description</span>}</p>
                    <p className="text-xs text-slate-600 mt-1">
                      {p.supplierCustomer && <>{p.supplierCustomer} · </>}
                      {p.quantity != null && <>Qty {p.quantity}{p.unitCost != null && <> @ {formatCurrency(p.unitCost)}</>} · </>}
                      {p.receiptNumber && <>Receipt #{p.receiptNumber}</>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xl font-mono font-bold text-slate-900">{formatCurrency(p.amount)}</p>
                    {p.paymentMethod && <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{p.paymentMethod}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                  <button onClick={() => openEdit(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d]" data-testid={`purchase-edit-${p.id}`}>
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => setDeleteTarget(p)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-red-300 hover:bg-red-50 text-xs font-semibold text-slate-600 hover:text-red-700" data-testid={`purchase-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TransactionForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSaved={fetchAll}
        initial={editing}
        types={types} categories={categories} taxTreatments={taxTreatments} events={events}
        prefillTypeId={productPurchaseType?.id}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this purchase?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="purchase-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Purchases;
